import React, { useCallback, useEffect } from "react";
import { useUtilityAdminTranslation } from "../../../../hooks/useUtilityAdminTranslation";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { RetryIcon } from "../../../../assets/icons";
import { FailedToLoadData } from "../../../../common/components/failed-to-load-data/FailedToLoadData";
import Button from "../../../../common/components/xepp/button/Button";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import StatusBadge from "../../../../common/components/xepp/status-badge/StatusBadge";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import { useCan } from "../../../../guards/useCan";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { RefundAttempt, RefundCase } from "../../../../model/refunds/refund";
import { useGetRefundDetailsQuery } from "../../../../service/refund-service";
import { FeatureFlagName } from "../../../../tenant/features/featureFlags";
import { formatCurrency } from "../../../../utils";
import { formatDateTime } from "../../../admin/bulk-purchase/utils";
import InitialsAvatar from "../../../customer/components/initials-avatar/InitialsAvatar";
import UtilityPageLayout from "../../component/layout/UtilityPageLayout";
import RetryRefundModal from "./components/RetryRefundModal";
import { REFUNDS_BASE_PATH } from "./RefundsList";
import styles from "./refunds.module.scss";
import { actorLabel, attemptStatusKey, attemptStatusVariant, attemptsLabel, channelKey, destinationKey, refundStatusKey, refundStatusVariant } from "./refunds.utils";
import { useRetryRefund } from "./useRetryRefund";

interface RefundsLocationState {
	refundsSearch?: string;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className={styles.field}>
		<span className={styles.fieldLabel}>{label}</span>
		<span className={styles.fieldValue}>{children}</span>
	</div>
);

/** One refunded payment: the original transaction, where the refund stands, and every attempt. */
const RefundDetailsPage: React.FC = () => {
	const { t } = useUtilityAdminTranslation();
	const { transactionId } = useParams<{ transactionId: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { can } = useCan();
	const { updateLayout } = useConfigLayout();

	const { data, isLoading, isError, refetch } = useGetRefundDetailsQuery(transactionId ?? "", { skip: !transactionId });
	const retry = useRetryRefund();

	const refundsSearch = (location.state as RefundsLocationState | null)?.refundsSearch;

	// Back goes to the list with its search and page intact rather than wherever history points.
	const handleNavigateBack = useCallback(() => {
		navigate({ pathname: REFUNDS_BASE_PATH, search: refundsSearch ? `?${refundsSearch}` : "" });
	}, [navigate, refundsSearch]);

	useEffect(() => {
		updateLayout({
			showCountrySelector: false,
			utility: null,
			showExportButton: false,
			showBreadCrumb: false,
			goBackHandler: handleNavigateBack,
			pageTitle: (
				<span>
					<span className={styles.breadcrumbCurrent}>{t("refunds.title")}</span>
					{" / "}
					<span className={styles.breadcrumbMuted}>{data?.reference ?? ""}</span>
				</span>
			),
		});

		return () => {
			updateLayout({ pageTitle: null, goBackHandler: null });
		};
	}, [updateLayout, handleNavigateBack, data?.reference, t]);

	const renderActor = (attempt: RefundAttempt) => {
		const label = actorLabel(attempt);
		return "name" in label ? label.name : t(`refunds.actor.${label.key}`);
	};

	const historyColumns: Array<ColumnType<RefundAttempt>> = [
		{ header: t("refunds.details.history.serial"), accessor: "attemptNumber" },
		{ header: t("refunds.details.history.actor"), accessor: "actorName", render: attempt => <span>{renderActor(attempt)}</span> },
		{ header: t("refunds.details.history.time"), accessor: "createdAt", render: attempt => <span>{formatDateTime(attempt.createdAt)}</span> },
		{
			header: t("refunds.details.history.result"),
			accessor: "status",
			render: attempt => (
				<StatusBadge variant={attemptStatusVariant(attempt.status)}>{t(`refunds.attemptStatus.${attemptStatusKey(attempt.status)}`)}</StatusBadge>
			),
		},
		{
			header: t("refunds.details.history.error"),
			accessor: "failureReason",
			render: attempt => (attempt.failureReason ? <span className={styles.errorText}>{attempt.failureReason}</span> : <span className={styles.muted}>–</span>),
		},
	];

	const renderBody = (refund: RefundCase) => {
		const customerName = refund.customerName ?? t("refunds.unknownCustomer");

		return (
			<div className={styles.detailsPage}>
				<div className={styles.sectionHeader}>
					<h2 className={styles.sectionTitle}>{t("refunds.details.customerDetails")}</h2>
					{refund.canRetry && can(UtilityPermissions.RetryRefund) && (
						<Button
							leftIcon={<RetryIcon className={styles.retryIcon} />}
							variant="primary"
							text={t("refunds.details.retryButton")}
							onClick={() => retry.requestRetry(refund)}
						/>
					)}
				</div>

				<div className={styles.detailsCard}>
					<div className={styles.detailsHeader}>
						<InitialsAvatar name={customerName} index={0} size={56} fontSize={16} />
						<span className={styles.customerName}>{customerName}</span>
						<div className={styles.headerMeta}>
							<span className={styles.metaLabel}>{t("refunds.details.paymentDate")}</span>
							<span className={styles.metaValue}>{formatDateTime(refund.paymentDate)}</span>
						</div>
					</div>

					<div className={styles.fieldGroup}>
						<Field label={t("refunds.details.reference")}>{refund.reference}</Field>
						<Field label={t("refunds.details.originalPayment")}>{formatCurrency(refund.amount.amount, { currency: refund.amount.currency })}</Field>
						<Field label={t("refunds.details.paymentChannel")}>{t(`refunds.channel.${channelKey(refund.paymentChannel)}`)}</Field>
						<Field label={t("refunds.details.refundDestination")}>{t(`refunds.destination.${destinationKey(refund.refundDestination)}`)}</Field>
					</div>

					<div className={styles.fieldGroup}>
						<Field label={t("refunds.details.refundAmount")}>{formatCurrency(refund.refundAmount.amount, { currency: refund.refundAmount.currency })}</Field>
						<Field label={t("refunds.details.attemptsUsed")}>{attemptsLabel(refund.attemptsUsed, refund.maxAttempts)}</Field>
						<Field label={t("refunds.details.adminAlertSent")}>
							{refund.adminAlertSentAt ? formatDateTime(refund.adminAlertSentAt) : <span className={styles.muted}>{t("refunds.details.notSent")}</span>}
						</Field>
						<Field label={t("refunds.details.status")}>
							<StatusBadge variant={refundStatusVariant(refund.status)}>{t(`refunds.status.${refundStatusKey(refund.status)}`)}</StatusBadge>
						</Field>
					</div>
				</div>

				<div className={styles.historyCard}>
					<h2 className={styles.sectionTitle}>{t("refunds.details.retryHistory")}</h2>
					<DataTable<RefundAttempt>
						data={refund.attempts ?? []}
						columns={historyColumns}
						emptyComponent={<EmptyState title={t("refunds.list.emptyTitle")} />}
					/>
				</div>

				<RetryRefundModal isOpen={retry.target !== null} isLoading={retry.isRetrying} onConfirm={retry.confirm} onCancel={retry.cancel} />
			</div>
		);
	};

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className={styles.centered} data-testid="refund-details-loader">
					<ClipLoader />
				</div>
			);
		}

		if (isError || !data) {
			return (
				<div className={styles.errorState}>
					<FailedToLoadData text={t("refunds.details.loadFailed")} />
					<Button text={t("refunds.details.tryAgain")} variant="outline" onClick={() => refetch()} />
				</div>
			);
		}

		return renderBody(data);
	};

	return (
		<UtilityPageLayout title="" feature={FeatureFlagName.ADMIN_REFUNDS}>
			{renderContent()}
		</UtilityPageLayout>
	);
};

export default RefundDetailsPage;
